import {
  Briefcase,
  Building2,
  CheckCircle2,
  ChevronDown,
  ExternalLink,
  Lock,
  Mail,
  Settings,
  User,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { SessionSwitcher } from "@/components/ui/session-switcher";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { NAVIGATION_DATA } from "@/config/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { useSessionSwitcher } from "@/contexts/SessionSwitcherContext";
import { useGetDecizieInregistrare } from "@/hooks/api/query/useGetDecizieInregistrare";
import { useGetEntityData } from "@/hooks/api/query/useGetEntityData";
import { useGetRiskAnalysisBySession } from "@/hooks/api/query/useGetRiskAnalysisBySession";
import { cn } from "@/lib/utils";
import type { SessionStatus } from "@/types/common";
import type { NavigationPage, PageStatus } from "@/types/navigation";
import { TouchTooltip } from "@/components/ui/touch-tooltip";
import {
  getLockedTooltip,
  resolvePageStatus,
} from "@/utils/guideProgressCalculator";
import { useOnboardingModal } from "@/contexts/OnboardingModalContext";

function getStatusIndicator(status?: string) {
  switch (status) {
    case "locked":
      return (
        <div className="w-4 h-4 flex items-center justify-center">
          <Lock size={12} className="text-ds-text-400" />
        </div>
      );
    case "completed":
      return (
        <div className="w-4 h-4 flex items-center justify-center">
          <CheckCircle2 size={14} className="text-ds-green-500" />
        </div>
      );
    case "current":
    case "in-progress":
    case "in_progress":
      return (
        <div className="relative w-4 h-4 flex items-center justify-center">
          <div className="absolute w-3 h-3 rounded-full bg-ds-blue-600/30 animate-pulse-ring" />
          <div className="relative w-2 h-2 bg-ds-blue-600 rounded-full" />
        </div>
      );
    case "not_started":
    case "pending":
      return "";
    default:
      return null;
  }
}

function getPageStyles(status?: PageStatus, isActive?: boolean): string {
  if (isActive) {
    return "bg-ds-blue-50 text-ds-blue-700 font-medium";
  }
  if (status === "locked") {
    return "text-ds-text-400 cursor-not-allowed hover:bg-transparent hover:text-ds-text-400";
  }
  return "text-ds-text-600 hover:bg-ds-surface-50";
}

export function AppSidebar() {
  const { state } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentSessionId, currentSession } = useSessionSwitcher();

  const { data: entityDataResponse } = useGetEntityData(currentSessionId, {
    enabled: !!currentSessionId && !!user,
    retry: 1,
  });

  const { data: riskData } = useGetRiskAnalysisBySession(currentSessionId, {
    enabled: !!currentSessionId && !!user,
    retry: false,
  });

  const { data: decizieData } = useGetDecizieInregistrare(currentSessionId, {
    enabled: !!currentSessionId && !!user,
    retry: false,
  });

  const { showOnboardingModal } = useOnboardingModal();
  const entityData = entityDataResponse?.entity_data;

  const currentPath = location.pathname;
  const isCollapsed = state === "collapsed";

  // Track which subsections are expanded - starts empty, populated by useEffect based on current path
  const [expandedSubsections, setExpandedSubsections] = useState<string[]>([]);

  const toggleSubsection = (id: string) => {
    setExpandedSubsections((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const getPageUrl = (page: NavigationPage): string | null => {
    if (page.dynamicUrl) {
      return page.dynamicUrl(currentSessionId);
    }
    return page.url || null;
  };

  const isPageActive = (page: NavigationPage) => {
    const url = getPageUrl(page);
    if (!url) return false;
    if (currentPath === url) return true;
    if (page.url === "/identificare-raportare" && currentPath === "/wizard")
      return true;
    if (page.dynamicUrl && page.url) {
      return currentPath.startsWith(page.url);
    }
    return false;
  };

  const shouldShowPage = (page: NavigationPage) => {
    if (page.requiresAuth && !user) return false;
    if (page.conditionalDisplay) {
      return page.conditionalDisplay(
        entityData,
        currentSessionId,
        (currentSession?.status as SessionStatus) ?? null
      );
    }
    return true;
  };

  const getActiveSubsectionId = (): string | null => {
    for (const subsection of NAVIGATION_DATA.subsections) {
      for (const page of subsection.pages) {
        if (!shouldShowPage(page)) continue;

        const url = getPageUrl(page);
        if (!url) continue;

        if (currentPath === url) return subsection.id;
        if (page.url === "/identificare-raportare" && currentPath === "/wizard")
          return subsection.id;
        if (page.dynamicUrl && page.url && currentPath.startsWith(page.url))
          return subsection.id;
      }
    }
    return null;
  };

  useEffect(() => {
    const activeId = getActiveSubsectionId();
    if (activeId && !expandedSubsections.includes(activeId)) {
      setExpandedSubsections((prev) =>
        prev.includes(activeId) ? prev : [...prev, activeId]
      );
    }
  }, [currentPath, currentSessionId]);

  const getResolvedStatus = (
    page: NavigationPage
  ): PageStatus | string | undefined => {
    return resolvePageStatus(page, entityData, riskData, decizieData);
  };

  const handlePageClick = (page: NavigationPage) => {
    if (page.externalLink) {
      window.open(page.externalLink, "_blank", "noopener,noreferrer");
      return;
    }
    if (getResolvedStatus(page) === "locked" || !page.url) return;
    if (
      page.id === "notificare-entitate" ||
      page.id === "formular-notificare"
    ) {
      showOnboardingModal();
      return;
    }
    navigate(page.url);
  };

  const getUserInitials = () => {
    if (!user?.email) return "U";
    return user.email.charAt(0).toUpperCase();
  };

  const getUserDisplayName = () => {
    if (!user?.email) return "Utilizator";
    const name = user.email.split("@")[0];
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  return (
    <Sidebar className="border-sidebar-border" collapsible="icon">
      {/* Header with gradient logo */}
      <SidebarHeader
        className={cn(
          "bg-navy-900 -mr-px relative z-10",
          isCollapsed ? "px-2 py-2.5 pr-[calc(0.5rem+1px)]" : "px-4 py-2.5 pr-[calc(1rem+1px)]"
        )}
      >
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <Link to="/" className="flex items-center gap-2">
              <span className="w-3.5 h-3.5 rounded-xs bg-ds-green-500 shrink-0" />
              <h1 className="font-heading text-lg font-bold text-white">
                NIS2PASS
              </h1>
            </Link>
          )}
          <SidebarTrigger
            className={cn(
              isCollapsed ? "mx-auto" : "ml-auto",
              "text-white hover:bg-white/10 hover:text-white transition-none"
            )}
          />
        </div>
      </SidebarHeader>

      <SidebarContent className={`${isCollapsed ? "px-1 py-3" : "p-3"}`}>
        {/* Session Switcher with Entity Card */}
        {!isCollapsed && user && (
          <div className="mb-3">
            <SessionSwitcher entityData={entityData} />
          </div>
        )}

        {/* Collapsed Entity Initial */}
        {isCollapsed && user && (
          <div className="mb-3 flex justify-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-10 h-10 bg-navy-900 rounded-xl flex items-center justify-center cursor-default">
                  {entityData?.nume_entitate ? (
                    <span className="text-sm font-semibold font-heading text-white">
                      {entityData?.nume_entitate
                        ? entityData.nume_entitate.charAt(0).toUpperCase()
                        : "?"}
                    </span>
                  ) : (
                    <Building2 size={20} className="text-ds-text-400" />
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{entityData?.nume_entitate || "Companie nouă"}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        {/* Navigation */}
        <nav className="space-y-1">
          {user && (() => {
            const isActive = currentPath === "/companiile-mele";
            if (isCollapsed) {
              return (
                <SidebarMenu>
                  <SidebarMenuItem className="flex justify-center">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <SidebarMenuButton
                          asChild
                          tooltip="Companiile mele"
                          className={cn(
                            "w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all",
                            isActive
                              ? "bg-ds-blue-50 text-ds-blue-700"
                              : "text-ds-text-600 hover:bg-ds-surface-50"
                          )}
                        >
                          <NavLink
                            to="/companiile-mele"
                            data-testid="sidebar-companiile-mele"
                          >
                            <Briefcase
                              size={18}
                              className={
                                isActive ? "text-ds-blue-600" : "text-ds-text-400"
                              }
                            />
                          </NavLink>
                        </SidebarMenuButton>
                      </TooltipTrigger>
                      <TooltipContent side="right">Companiile mele</TooltipContent>
                    </Tooltip>
                  </SidebarMenuItem>
                </SidebarMenu>
              );
            }
            return (
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    tooltip="Companiile mele"
                    className={cn(
                      "w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all",
                      isActive
                        ? "bg-ds-blue-50 text-ds-blue-700"
                        : "text-ds-text-600 hover:bg-ds-surface-50"
                    )}
                  >
                    <NavLink
                      to="/companiile-mele"
                      data-testid="sidebar-companiile-mele"
                    >
                      <div className="flex items-center gap-3">
                        <Briefcase
                          size={18}
                          className={
                            isActive ? "text-ds-blue-600" : "text-ds-text-400"
                          }
                        />
                        <span className="font-medium font-heading text-sm">
                          Companiile mele
                        </span>
                      </div>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            );
          })()}

          {NAVIGATION_DATA.subsections.map((subsection) => {
            const SubsectionIcon = subsection.icon;
            const isExpanded = expandedSubsections.includes(subsection.id);
            const hasActiveChild = subsection.pages.some(
              (page) => shouldShowPage(page) && isPageActive(page)
            );

            // Filter visible pages
            const visiblePages = subsection.pages.filter(shouldShowPage);
            if (visiblePages.length === 0) return null;

            return (
              <Collapsible
                key={subsection.id}
                open={isExpanded}
                onOpenChange={() => toggleSubsection(subsection.id)}
              >
                <SidebarMenu>
                  <SidebarMenuItem
                    className={isCollapsed ? "flex justify-center" : ""}
                  >
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton
                        tooltip={subsection.title}
                        className={cn(
                          "w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all",
                          hasActiveChild
                            ? "bg-ds-blue-50 text-ds-blue-700"
                            : "text-ds-text-600 hover:bg-ds-surface-50"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <SubsectionIcon
                            size={18}
                            className={
                              hasActiveChild
                                ? "text-ds-blue-600"
                                : "text-ds-text-400"
                            }
                          />
                          {!isCollapsed && (
                            <span className="font-medium font-heading text-sm">
                              {subsection.title}
                            </span>
                          )}
                        </div>
                        {!isCollapsed && (
                          <ChevronDown
                            size={16}
                            className={cn(
                              isExpanded ? "rotate-180" : "",
                              hasActiveChild
                                ? "text-ds-blue-400"
                                : "text-ds-text-400"
                            )}
                          />
                        )}
                      </SidebarMenuButton>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <SidebarMenuSub className="mt-1 ml-4 pl-4 border-l-2 border-ds-border-200 space-y-0.5">
                        {visiblePages.map((page) => {
                          const PageIcon = page.icon;
                          const isActive = isPageActive(page);
                          const status = getResolvedStatus(page);
                          const isLocked = status === "locked";
                          const tooltipText = isLocked
                            ? getLockedTooltip(page.id)
                            : null;
                          const pageUrl = getPageUrl(page);
                          const shouldShowModal =
                            page.id === "notificare-entitate" ||
                            page.id === "formular-notificare";
                          const canNavigate =
                            !isLocked && !page.externalLink && !!pageUrl;

                          const menuContent = shouldShowModal ? (
                            <button
                              type="button"
                              onClick={() => handlePageClick(page)}
                              className="flex items-center justify-between w-full"
                            >
                              <div className="flex items-center gap-2">
                                <PageIcon size={14} />
                                <span>{page.shortTitle || page.title}</span>
                              </div>
                              {getStatusIndicator(status)}
                            </button>
                          ) : canNavigate ? (
                            <NavLink
                              to={pageUrl}
                              className="flex items-center justify-between w-full"
                            >
                              <div className="flex items-center gap-2">
                                <PageIcon size={14} />
                                <span>{page.shortTitle || page.title}</span>
                              </div>
                              {getStatusIndicator(status)}
                            </NavLink>
                          ) : (
                            <div className="flex items-center justify-between w-full">
                              <div className="flex items-center gap-2">
                                <PageIcon size={14} />
                                <span>{page.shortTitle || page.title}</span>
                                {page.externalLink && (
                                  <ExternalLink
                                    size={10}
                                    className="text-ds-text-400"
                                  />
                                )}
                              </div>
                              {getStatusIndicator(status)}
                            </div>
                          );

                          const isAsChild = canNavigate || shouldShowModal;

                          const menuButton = (
                            <SidebarMenuSubButton
                              asChild={isAsChild}
                              className={cn(
                                "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all",
                                getPageStyles(status as PageStatus, isActive)
                              )}
                              onClick={() => {
                                if (page.externalLink || isLocked) {
                                  handlePageClick(page);
                                }
                              }}
                            >
                              {menuContent}
                            </SidebarMenuSubButton>
                          );

                          return (
                            <SidebarMenuSubItem key={page.id}>
                              {tooltipText ? (
                                <TouchTooltip content={tooltipText}>
                                  {menuButton}
                                </TouchTooltip>
                              ) : (
                                menuButton
                              )}
                            </SidebarMenuSubItem>
                          );
                        })}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </SidebarMenu>
              </Collapsible>
            );
          })}
        </nav>

        <div
          className={cn(
            "mt-auto pt-2",
            isCollapsed ? "flex justify-center" : "px-3"
          )}
        >
          {isCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <NavLink
                  to="/contact"
                  className={cn(
                    "flex items-center justify-center w-10 h-10 rounded-xl transition-colors",
                    currentPath === "/contact"
                      ? "bg-ds-blue-100 text-ds-blue-600"
                      : "text-ds-text-500 hover:text-ds-blue-600 hover:bg-ds-surface-50"
                  )}
                >
                  <Mail size={18} />
                </NavLink>
              </TooltipTrigger>
              <TooltipContent side="right">Contact</TooltipContent>
            </Tooltip>
          ) : (
            <NavLink
              to="/contact"
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-heading transition-colors",
                currentPath === "/contact"
                  ? "bg-ds-blue-50 text-ds-blue-700 font-medium"
                  : "text-ds-text-500 hover:text-ds-blue-600 hover:bg-ds-surface-50"
              )}
            >
              <Mail size={18} />
              <span>Contact</span>
            </NavLink>
          )}
        </div>
      </SidebarContent>

      {/* User Profile Footer */}
      <SidebarFooter
        className={cn(
          "border-t border-ds-border-200",
          isCollapsed ? "py-4 px-1" : "p-3"
        )}
      >
        {!isCollapsed ? (
          <div className="flex items-center gap-3 p-0.5 rounded-xl bg-ds-surface-50">
            <div className="w-8 h-8 bg-ds-blue-100 rounded-full flex items-center justify-center">
              <User size={16} className="text-ds-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium font-heading text-navy-900 truncate">
                {getUserDisplayName()}
              </p>
              <p className="text-xs text-ds-text-500 truncate">
                {user ? "Utilizator" : "Neautentificat"}
              </p>
            </div>
            <button
              onClick={() => navigate("/profil")}
              className="p-1.5 hover:bg-ds-border-200 rounded-lg transition-colors"
            >
              <Settings size={16} className="text-ds-text-400" />
            </button>
          </div>
        ) : (
          <SidebarMenuButton
            tooltip="Profil"
            onClick={() => navigate("/profil")}
            className="mx-auto group-data-[collapsible=icon]:!p-0"
          >
            <div className="w-8 h-8 bg-ds-blue-100 rounded-full flex items-center justify-center">
              <span className="text-xs font-semibold font-heading text-ds-blue-600">
                {getUserInitials()}
              </span>
            </div>
          </SidebarMenuButton>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
